import {messages} from '@smartmixin/i18n';
import React from 'react';
import {useIntl} from 'react-intl';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {Location} from '../api/types';
import {
  addFavoriteLocation,
  addLocation,
  getFavoriteLocations,
  getLocationList,
  removeFavoriteLocation,
  reorderFavoriteLocations,
  setAlert,
} from '../store';
import {CommonRootState} from '../store/types';
import {trackCoreEvent} from '../utils/Analytics/sink';

/**
 * The most favourite places anyone may hold, signed in or out, whatever the
 * flags (Vincent, 2026-10-25: My weather, FE #381, does not lift it).
 */
const MAX_FAVORITE_LOCATIONS = 5;

const useFavoriteLocation = () => {
  const {formatMessage} = useIntl();
  const dispatch = useDispatch();

  const favoriteLocations = useSelector(getFavoriteLocations);
  // Read when adding, not subscribed to: every consumer would otherwise
  // re-render, and get a new addFavorite, on each change to the list.
  const store = useStore<CommonRootState>();

  const addFavorite = React.useCallback(
    (location: Location) => {
      if (favoriteLocations.some((loc) => loc.hash === location.hash)) {
        return;
      }

      if (favoriteLocations.length >= MAX_FAVORITE_LOCATIONS) {
        dispatch(
          setAlert({
            type: 'ERROR',
            message: formatMessage(
              messages.stations.maxFavoriteLocationsReached,
            ),
          }),
        );
        return;
      }

      // A favourite is a hash into the location list. One the list does not
      // hold yet (the point the GPS star saves, FE #388) is stored first, and
      // only past the cap, so a refused add leaves no stray recent.
      const locationList = getLocationList(store.getState());
      if (!locationList.some((loc) => loc.hash === location.hash)) {
        dispatch(addLocation(location));
      }
      dispatch(addFavoriteLocation(location.hash));
      trackCoreEvent({name: 'favorite_add', params: {kind: 'place'}});
      dispatch(
        setAlert({
          type: 'INFO',
          message: formatMessage(messages.stations.locationAddedToFavorites),
        }),
      );
    },
    [dispatch, favoriteLocations, formatMessage, store],
  );

  const removeFavorite = React.useCallback(
    (location: Location) => {
      if (favoriteLocations.some((loc) => loc.hash === location.hash)) {
        dispatch(removeFavoriteLocation(location.hash));
        trackCoreEvent({name: 'favorite_remove', params: {kind: 'place'}});
        dispatch(
          setAlert({
            type: 'INFO',
            message: formatMessage(
              messages.stations.locationRemovedFromFavorites,
            ),
          }),
        );
      }
    },
    [dispatch, favoriteLocations, formatMessage],
  );

  const reorderFavorites = React.useCallback(
    (orderedLocations: Location[]) => {
      const orderedHashes = orderedLocations.map((location) => location.hash);
      dispatch(reorderFavoriteLocations(orderedHashes));
    },
    [dispatch],
  );

  return {addFavorite, removeFavorite, reorderFavorites, favoriteLocations};
};

export default useFavoriteLocation;
